import React from 'react';
import { Card, Button } from 'react-bootstrap';
import { ImageGallery } from '../../../../components/common/ImageGallery';
import { CpBatchData } from '../../../catalog/ss-editor/components/CpBatchData';
import { IdBadge, IdBadgeType } from '../../../../components/ui/badge/IdBadge';
import { StatusBadge } from '../../../../components/ui/badge/StatusBadge';
import { type SkinSeoulAdminProduct, SkinSeoulProductType } from '../../../../models/ProductTypes';
import { getConfig } from '../../../../config';

interface AdminProductCardProps {
  readonly product: SkinSeoulAdminProduct;
  readonly index: number;
  readonly isSelected: boolean;
  readonly onSelect: (product: SkinSeoulAdminProduct) => void;
  readonly isVariation?: boolean;
  readonly parentIndex?: number;
  readonly variationIndex?: number;
  readonly showImage?: boolean;
  readonly showQuickLinks?: boolean;
}

export const AdminProductCard: React.FC<AdminProductCardProps> = ({
  product,
  index,
  isSelected,
  onSelect,
  isVariation = false,
  parentIndex,
  variationIndex,
  showImage = true,
  showQuickLinks = true,
}) => {
  return (
    <Card
      className="mb-2 shadow-sm"
      style={{
        backgroundColor: '#fff',
        marginLeft: isVariation ? '2rem' : '0',
        overflow: 'hidden',
        cursor: 'pointer',
        border: isSelected ? '2px solid #6c757d' : '1px solid #dee2e6',
      }}
      onClick={() => onSelect(product)}
    >
      <Card.Body className="p-2">
        <div className="d-flex">
          {/* Number/Hierarchy indicator */}
          <div className="me-2 flex-shrink-0 d-flex align-items-start">
            {isVariation && (
              <small className="text-muted me-1" style={{ fontSize: '0.7rem', fontWeight: 'bold', marginTop: '2px' }}>
                ↳
              </small>
            )}
            <span style={{ fontSize: '0.8rem', fontWeight: 'bold', marginTop: '2px', color: '#6c757d' }}>
              {isVariation && parentIndex !== undefined && variationIndex !== undefined
                ? `${parentIndex + 1}.${variationIndex + 1}`
                : index + 1}
            </span>
          </div>

          {/* Image and Gallery - Only for non-variations */}
          {showImage && !isVariation && (
            <div className="me-3 flex-shrink-0">
              <ImageGallery
                productImages={product.productImageUrls}
                learnMoreImages={product.learnMoreUrl}
                title={`${product.name} - Image Gallery`}
              />
            </div>
          )}

          {/* Details */}
          <div className="flex-grow-1 position-relative" style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
            {/* Badges */}
            <div className="d-flex gap-2 flex-wrap mb-2">
              <StatusBadge type="productType" value={product.type || 'simple'} size="md" />
              <StatusBadge type="publication" value={product.postStatus || 'draft'} size="md" />
              <StatusBadge type="stock" value={product.status || 'outofstock'} size="md" />
              {!isVariation && product.attributes?.wave && (
                <StatusBadge
                  type="wave"
                  value={product.attributes.wave}
                  label={`W${product.attributes.wave}`}
                  size="md"
                />
              )}
            </div>

            {/* Copiable Badges */}
            <div className="mb-2 d-flex gap-1 flex-wrap">
              <IdBadge type={IdBadgeType.SKU} value={product.sku} size={isVariation ? 'sm' : 'md'} stopPropagation />
              <IdBadge
                type={IdBadgeType.SPI}
                value={product.productId || 0}
                size={isVariation ? 'sm' : 'md'}
                stopPropagation
              />
              {product.attributes?.gp_product_id && (
                <IdBadge
                  type={IdBadgeType.GPI}
                  value={product.attributes.gp_product_id}
                  size={isVariation ? 'sm' : 'md'}
                  stopPropagation
                  truncate
                  title="Click to copy GP Product ID"
                />
              )}
              {product.attributes?.cp_parent_code && (
                <IdBadge
                  type={IdBadgeType.CPPI}
                  value={product.attributes.cp_parent_code}
                  size={isVariation ? 'sm' : 'md'}
                  stopPropagation
                  title="Click to copy CP Parent Code"
                />
              )}
            </div>

            {/* Category & Brand - Only for non-variations */}
            {!isVariation && (
              <div className="mb-2">
                <div className="mb-1">
                  {product.brand && product.brand.toLowerCase() !== 'none' ? (
                    <StatusBadge type="brand" label={product.brand} size="md" />
                  ) : (
                    <div className="d-flex align-items-center gap-2">
                      <StatusBadge type="brand" label="No Brand" size="md" />
                      <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>
                        ⚠️ Alert
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                  {product.category.join(' › ')}
                </div>
              </div>
            )}

            {/* Product Name */}
            <div className="mb-2">
              <h6
                className="fw-bold mb-1"
                style={{
                  fontSize: isVariation ? '0.9rem' : '1rem',
                  lineHeight: '1.2',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                {product.name}
              </h6>
              {product.nameKor && (
                <div
                  className="text-muted"
                  style={{
                    fontSize: isVariation ? '0.85rem' : '0.9rem',
                    lineHeight: '1.2',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {product.nameKor}
                </div>
              )}
            </div>

            {/* Description */}
            {!isVariation && (
              <div className="mb-2">
                <small className="text-muted me-1">Description:</small>
                {product.description ? (
                  <span
                    className="text-muted"
                    style={{
                      fontSize: '0.85rem',
                      lineHeight: '1.3',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                    }}
                  >
                    {product.description}
                  </span>
                ) : (
                  <div className="d-flex align-items-center gap-2">
                    <small className="text-muted">No Description</small>
                    <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>
                      ⚠️ Alert
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Size, Pack, Color */}
            <div className="d-flex gap-2 mb-2 flex-wrap">
              {product['size'] && (
                <div className="d-flex align-items-center">
                  <small className="text-muted me-1">Size:</small>
                  <small>{product['size']}</small>
                </div>
              )}
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted me-1">Pack:</small>
                {product['pack'] ? (
                  <small>{product['pack']}</small>
                ) : (
                  <>
                    <small className="text-muted">Missing</small>
                    <span className="badge bg-warning text-dark" style={{ fontSize: '0.65rem' }}>
                      ⚠️ Alert
                    </span>
                  </>
                )}
              </div>
              {product.color && (
                <div className="d-flex align-items-center">
                  <small className="text-muted me-1">Color:</small>
                  <small>{product.color}</small>
                </div>
              )}
            </div>

            {/* Price - Variable products don't have independent pricing */}
            {product.type !== SkinSeoulProductType.Variable && (
              <div className="d-flex align-items-center gap-2 mb-2">
                {product.originalPrice ? (
                  <>
                    {product.salePrice ? (
                      <>
                        <span className="text-danger me-2">
                          {product.currency || 'SGD'} {product.salePrice.toFixed(2)}
                        </span>
                        <span className="text-muted text-decoration-line-through" style={{ fontSize: '0.8rem' }}>
                          {product.currency || 'SGD'} {product.originalPrice.toFixed(2)}
                        </span>
                      </>
                    ) : (
                      <span className="text-dark" style={{ fontSize: '0.8rem' }}>
                        {product.currency || 'SGD'} {product.originalPrice.toFixed(2)}
                      </span>
                    )}
                  </>
                ) : (
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                      No Regular Price
                    </span>
                    <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>
                      ⚠️ Alert
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Coupang Data */}
            <div className="mb-2">
              <CpBatchData
                cpListPrice={product.attributes?.cp_list_price ?? undefined}
                cpProductStatus={product.attributes?.cp_product_status ?? undefined}
                cpShippingDate={product.attributes?.cp_shipping_date ?? undefined}
                cpLastScrapedAt={product.attributes?.cp_last_scraped_at ?? undefined}
                cpLastError={product.attributes?.cp_last_error ?? undefined}
              />
            </div>

            {/* Total Sales - Only for non-variations */}
            {!isVariation && (
              <div className="mb-2">
                <small className="text-muted">
                  Total Sales:{' '}
                  <strong>
                    {product.attributes?.total_sales !== undefined ? product.attributes.total_sales : '-'}
                  </strong>
                </small>
              </div>
            )}

            {/* Product Link */}
            {!isVariation && product.productLink && (
              <div className="mb-2">
                <small className="text-muted me-1">Product Link:</small>
                <a
                  href={product.productLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-primary"
                  style={{
                    fontSize: '0.8rem',
                    textDecoration: 'none',
                    wordBreak: 'break-all',
                  }}
                >
                  {product.productLink}
                </a>
                {(product.productLink.endsWith('product/') ||
                  product.productLink.endsWith('product') ||
                  product.productLink.endsWith('product//')) && (
                  <span
                    className="badge bg-warning text-dark ms-2"
                    style={{ fontSize: '0.7rem', cursor: 'help' }}
                    title="Product must have a valid URL with product name slug to publish. You can publish via WP Admin or CM Workflow step 3 or step 8"
                  >
                    ⚠️ Alert
                  </span>
                )}
              </div>
            )}

            {/* Quick Links - Only for Variable/Simple and when enabled */}
            {showQuickLinks &&
              !isVariation &&
              (product.type === SkinSeoulProductType.Variable || product.type === SkinSeoulProductType.Simple) && (
                <div className="d-flex gap-2 mt-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const config = getConfig();
                      window.open(`${config.wpAdminUrl}post.php?post=${product.productId}&action=edit`, '_blank');
                    }}
                  >
                    Go to WP Admin
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`/catalog/ss-editor?sku=${product.sku}`, '_blank');
                    }}
                  >
                    Go to SS Editor
                  </Button>
                  {product.attributes?.cp_url && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(product.attributes?.cp_url, '_blank');
                      }}
                    >
                      Go to CP
                    </Button>
                  )}
                  {product.attributes?.gp_product_id && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const gpi = product.attributes?.gp_product_id;
                        window.open(`/sourcing/matched/gpi/${gpi}`, '_blank');
                      }}
                    >
                      Go to GP-CP Matched
                    </Button>
                  )}
                  <Button
                    variant="outline-info"
                    size="sm"
                    style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const config = getConfig();
                      window.open(`${config.apiUrl}/products/admin?skus=${product.sku}`, '_blank');
                    }}
                  >
                    Debug API
                  </Button>
                  {product.productLink && (
                    <Button
                      variant="outline-info"
                      size="sm"
                      style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const config = getConfig();
                        const slug = product.productLink?.split('/product/')[1]?.replace(/\/$/, '');
                        window.open(`${config.apiUrl}/page/productDetail?slug=${slug}&currency=SGD`, '_blank');
                      }}
                    >
                      Debug Page
                    </Button>
                  )}
                </div>
              )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
